export const HR_DEPT_ID = "hr-dept";

/** Frontend role ids for HR family */
export const HR_ROLE = "hr";
export const HR_STAFF_ROLE = "hr-staff";
export const OWNER_ROLE = "owner";

const ROLE_HIERARCHY_FOR_PICK = {
  employee: 1,
  hr_staff: 1,
  manager: 2,
  team_leader: 2,
  supervisor: 2,
  branch_manager: 3,
  regional_manager: 4,
  department_manager: 5,
  hr: 6,
  gm: 7,
  owner: 7,
  admin: 8,
};

/** Pick highest-authority backend role slug for session mapping. */
export function pickPrimaryBackendRole(roles = []) {
  if (!roles.length) return "employee";
  return [...roles].sort(
    (a, b) =>
      (ROLE_HIERARCHY_FOR_PICK[b] ?? 0) - (ROLE_HIERARCHY_FOR_PICK[a] ?? 0),
  )[0];
}

export function isHrStaffRole(role) {
  return role === HR_STAFF_ROLE || role === "hr_staff";
}

export function isPrimaryHrRole(role) {
  return role === HR_ROLE;
}

/** Employee base salary on the employees screen — primary HR and admin only. */
export function canViewEmployeeSalary(role, backendRoles = []) {
  const roles = Array.isArray(backendRoles) ? backendRoles : [];
  return (
    isPrimaryHrRole(role) ||
    role === "admin" ||
    roles.includes(HR_ROLE) ||
    roles.includes("admin")
  );
}

export function isHrFamilyRole(role) {
  return isPrimaryHrRole(role) || isHrStaffRole(role);
}

/** Sick leave remaining is visible to HR only — not employees, managers, or GM. */
export function canViewSickLeaveRemaining(role) {
  return isHrFamilyRole(role);
}

/**
 * Any account in the HR department (except primary HR / GM / Admin)
 * uses the HR Staff UI.
 */
export function resolveEffectiveFrontendRole(
  mappedRole,
  { departmentCode, departmentId, backendRoles = [] } = {},
) {
  if (
    mappedRole === "hr" ||
    mappedRole === "gm" ||
    mappedRole === "owner" ||
    mappedRole === "admin" ||
    backendRoles.includes("hr") ||
    backendRoles.includes("gm") ||
    backendRoles.includes("owner") ||
    backendRoles.includes("admin")
  ) {
    return mappedRole;
  }

  const inHrDept =
    departmentCode === "HR" ||
    departmentId === HR_DEPT_ID ||
    backendRoles.includes("hr_staff");

  if (inHrDept) {
    return HR_STAFF_ROLE;
  }

  return mappedRole;
}

export function isOwnerRole(role) {
  return role === OWNER_ROLE || role === "owner";
}

export function isCompanyWideViewerRole(role) {
  return isOwnerRole(role) || role === "gm" || role === "admin";
}

/**
 * Primary HR / GM / Admin only — used for advances and appeals decisions.
 * Workflow request approvals for HR staff use isHrFamilyRole / backend perms instead.
 * Owners are view-only and cannot approve.
 */
export function canHrApproveRequests(role) {
  return isPrimaryHrRole(role) || role === "gm" || role === "admin";
}

export function canAccessZktPayroll(role) {
  return isPrimaryHrRole(role) || role === "gm" || isOwnerRole(role);
}

export function canManageApprovalCycles(role) {
  return isPrimaryHrRole(role) || role === "gm" || role === "admin";
}

export function isSameAccount(employee, user) {
  if (!employee || !user) return false;
  return employee.id === user.id || employee.code === user.code;
}

export function isHrDepartmentManager(employee) {
  return (
    employee?.departmentId === HR_DEPT_ID &&
    employee?.memberLevel === "department-manager"
  );
}

export function isGmOnlyMember(employee, isHrLoginAccount) {
  return isHrLoginAccount(employee) || isHrDepartmentManager(employee);
}

export function canManageEmployee({
  viewerRole,
  viewer,
  employee,
  isHrLoginAccount,
}) {
  if (viewerRole === "gm" || viewerRole === "admin") return true;
  if (!isHrFamilyRole(viewerRole)) return false;
  if (isSameAccount(employee, viewer)) return false;
  if (isGmOnlyMember(employee, isHrLoginAccount)) return false;
  if (employee?.memberLevel === "owner" || employee?.roleSlugs?.includes("owner")) {
    return false;
  }
  return true;
}

export function canHrAddMember({ viewerRole, viewer, form }) {
  if (viewerRole === "gm" || viewerRole === "admin") return { allowed: true };

  if (!isHrFamilyRole(viewerRole)) {
    return { allowed: false, message: "ليس لديك صلاحية إضافة أعضاء" };
  }

  if (form.code && viewer?.code && form.code.trim() === viewer.code.trim()) {
    return { allowed: false, message: "لا يمكنك إضافة حسابك الشخصي" };
  }

  if (
    form.memberType === "department-manager" &&
    form.departmentId === HR_DEPT_ID
  ) {
    return {
      allowed: false,
      message: "إضافة موارد بشرية — صلاحية المدير العام فقط",
    };
  }

  return { allowed: true };
}
